/**
 * Sync State Store
 *
 * Tracks which connections are actively syncing.
 * Updated by SSE events (sync.started, sync.completed, sync.failed).
 * Used by ConnectionCard to show real-time sync status.
 *
 * Why Zustand instead of React Query?
 * - sync.started doesn't change cacheable data
 * - Need immediate UI state update, not data refetch
 * - State is ephemeral (not persisted, not fetched from API)
 *
 * WP-RT-2: Frontend Real-Time Integration
 */
import { create } from 'zustand';

interface SyncState {
  /**
   * Set of connection IDs currently syncing
   */
  syncingConnections: Set<string>;

  /**
   * Mark a connection as syncing or not syncing
   */
  setSyncing: (connectionId: string, syncing: boolean) => void;

  /**
   * Check if a connection is syncing
   */
  isSyncing: (connectionId: string) => boolean;

  /**
   * Clear all sync state (e.g., on logout)
   */
  clearAll: () => void;
}

export const useSyncStore = create<SyncState>((set, get) => ({
  syncingConnections: new Set(),

  setSyncing: (connectionId: string, syncing: boolean) => {
    set((state) => {
      const newSet = new Set(state.syncingConnections);
      if (syncing) {
        newSet.add(connectionId);
      } else {
        newSet.delete(connectionId);
      }
      return { syncingConnections: newSet };
    });
  },

  isSyncing: (connectionId: string) => {
    return get().syncingConnections.has(connectionId);
  },

  clearAll: () => {
    set({ syncingConnections: new Set() });
  },
}));

export default useSyncStore;
